import { ReactNode, useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useApplication, NEW_APPLICATION } from "@/contexts/ApplicationContext";
import { PATH_TO_STAGE_MAP } from "@/hooks/useApplicationStage";
import { useActiveJob } from "@/contexts/ActiveJobContext";
import { Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { signOut } from "@/services/authService";

// Public routes that are always accessible regardless of application stage
const PUBLIC_ROUTES = [
  "/",
  "/login",
  "/register",
  "/forgot-password",
  "/reset-password",
  "/complete",
  "/rejected",
];

interface StageRestrictedRouteProps {
  children: ReactNode;
}

/**
 * A component that restricts navigation to only the current application stage
 * or public routes. If the user tries to access a stage they haven't reached yet,
 * they will be redirected to their current stage.
 */
const StageRestrictedRoute = ({ children }: StageRestrictedRouteProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const {
    application,
    refetch,
    setApplication,
    isLoading: isApplicationLoading,
  } = useApplication();
  const { job, isLoading: isJobLoading } = useActiveJob();
  const [isReady, setIsReady] = useState(false);
  const [hasHandledAuthError, setHasHandledAuthError] = useState(false);
  // Refetch application data and wait for application and job data to be fully loaded
  useEffect(() => {
    const loadData = async () => {
      // Always refetch application data to avoid stale data
      if (refetch) {
        await refetch();
      }
      console.log("Application loading:", isApplicationLoading);
      console.log("Job loading:", isJobLoading);
      console.log("Application:", application);
      console.log("Job:", job);

      if (!isApplicationLoading && !isJobLoading && application && job) {
        setIsReady(true);
      } else if (
        !isApplicationLoading &&
        !isJobLoading &&
        (!application || !job)
      ) {
        // If required data is missing after loading, sign out and redirect to login with a toast
        if (!hasHandledAuthError) {
          setHasHandledAuthError(true);
          try {
            // Clear any stored application/job identifiers
            localStorage.removeItem("currentApplicationId");
            localStorage.removeItem("currentJobId");

            // Sign out from auth and reset application state
            await signOut();
            setApplication(NEW_APPLICATION);
          } catch (err) {
            console.error("Error during forced sign out:", err);
          } finally {
            toast({
              title: "Something went wrong",
              description:
                "Something went wrong on our end. Please try logging in again.",
              variant: "destructive",
            });
            navigate("/login", { replace: true });
          }
        }
      }
    };

    loadData();
  }, [
    refetch,
    application,
    job,
    isApplicationLoading,
    isJobLoading,
    hasHandledAuthError,
    navigate,
    setApplication,
    toast,
  ]);

  // Show loading state while waiting for data
  if (!isReady) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Get all stages from job
  const allStages = job?.stages.map((stage) => stage.id) || [];
  console.log(allStages);
  // Current path and current application stage
  const currentPath = location.pathname;
  const currentApplicationStage = application?.currentStage;
  console.log(currentApplicationStage);
  // If the path is a public route, allow access
  if (PUBLIC_ROUTES.includes(currentPath)) {
    return <>{children}</>;
  }

  if (currentApplicationStage === "hold" && currentPath !== "/hold") {
    return <Navigate to="/hold" replace />;
  }
  if (currentApplicationStage === "rejected" && currentPath !== "/rejected") {
    return <Navigate to="/rejected" replace />;
  }
  if (currentApplicationStage === "pending" && currentPath !== "/pending") {
    return <Navigate to="/pending" replace />;
  }

  // Find the stage that corresponds to the current path
  const pathStage = Object.entries(PATH_TO_STAGE_MAP).find(
    ([path]) => currentPath === path
  );

  // If no matching stage is found, allow access (might be an admin route or other special case)
  if (!pathStage) {
    return <>{children}</>;
  }

  const [_, requestedStage] = pathStage;

  // At this point we know application and job data are loaded
  // Double-check that we have the current stage
  if (!currentApplicationStage) {
    console.warn("Application loaded but currentStage is missing");
    return <Navigate to="/" replace />;
  }

  // Find the index of the current stage and requested stage in the ordered stages array
  const currentStageIndex = allStages.indexOf(currentApplicationStage);
  const requestedStageIndex = allStages.indexOf(requestedStage);

  // If the requested stage is the current stage, allow access
  if (requestedStageIndex === currentStageIndex) {
    return <>{children}</>;
  }

  // Otherwise, redirect to the path corresponding to the current stage
  const currentStagePath = Object.entries(PATH_TO_STAGE_MAP).find(
    ([_, stage]) => stage === currentApplicationStage
  )?.[0];
  console.log("Current stage path:", currentStagePath);
  // If we can't determine the current stage path, redirect to home
  if (!currentStagePath) {
    return <Navigate to="/" replace />;
  }

  // Redirect to the current stage path
  return <Navigate to={currentStagePath} replace />;
};

export default StageRestrictedRoute;
